import React, { useState, useEffect, useRef } from "react";
import { Check, Shield, Lock, Bell, Upload, X, AlertCircle, ShieldCheck, Clock, FileText, IdCard } from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const Toggle = ({ checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    className={`relative w-10 h-6 rounded-full transition-colors ${checked ? "bg-[#1c3df5]" : "bg-white/15"}`}
  >
    <span
      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
        checked ? "translate-x-4" : "translate-x-0.5"
      }`}
    ></span>
  </button>
);

const kycBadge = {
  unverified: { color: "text-white/55 bg-white/5", icon: Shield, label: "Not verified" },
  pending: { color: "text-amber-400 bg-amber-400/10", icon: Clock, label: "Pending review" },
  verified: { color: "text-emerald-400 bg-emerald-400/10", icon: ShieldCheck, label: "Verified" },
  rejected: { color: "text-rose-400 bg-rose-400/10", icon: X, label: "Rejected" },
};

const DOC_TYPES = [
  { id: "passport", label: "Passport" },
  { id: "id_card", label: "National ID card" },
  { id: "drivers_license", label: "Driver's license" },
];

const fileToB64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = reader.result.toString();
      const stripped = s.replace(/^data:image\/[^;]+;base64,/, "");
      resolve(stripped);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const KycUploader = ({ label, onChange, hasFile }) => {
  const ref = useRef();
  const [previewing, setPreviewing] = useState(null);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_000_000) {
      alert("Image must be under 2MB");
      return;
    }
    const b64 = await fileToB64(f);
    setPreviewing(`data:image/jpeg;base64,${b64}`);
    onChange(b64);
  };

  return (
    <div>
      <div className="text-[11px] text-white/55 mb-1.5">{label}</div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="w-full px-3 py-6 rounded-xl border border-dashed border-white/15 hover:border-[#1c3df5] hover:bg-white/[0.02] text-center transition-all"
      >
        {previewing ? (
          <img src={previewing} alt="preview" className="max-h-32 mx-auto rounded-lg" />
        ) : hasFile ? (
          <div className="text-[12px] text-emerald-400 inline-flex items-center gap-1.5">
            <Check size={13} /> Submitted
          </div>
        ) : (
          <div className="text-[12px] text-white/55 inline-flex items-center gap-1.5">
            <Upload size={13} /> Click to upload (JPG/PNG, max 2MB)
          </div>
        )}
      </button>
      <input ref={ref} type="file" accept="image/*" onChange={onPick} className="hidden" />
    </div>
  );
};

const Settings = () => {
  const { user, refreshMe } = useAuth();
  const [emailNotif, setEmailNotif] = useState(true);
  const [pushNotif, setPushNotif] = useState(true);
  const [twoFA, setTwoFA] = useState(false);
  const [saved, setSaved] = useState(false);

  const [kyc, setKyc] = useState(null);
  const [kycMethod, setKycMethod] = useState("document"); // "nin" | "document"
  const [docType, setDocType] = useState("passport");
  const [docNumber, setDocNumber] = useState("");
  const [nin, setNin] = useState("");
  const [docImage, setDocImage] = useState(null);
  const [addrImage, setAddrImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  const loadKyc = async () => {
    try {
      const { data } = await api.get("/me/kyc");
      setKyc(data);
      if (data.submission) {
        const m = data.submission.method || (data.submission.doc_type === "nin" ? "nin" : "document");
        setKycMethod(m);
        if (m === "nin") {
          setNin(data.submission.doc_number || "");
        } else {
          setDocType(data.submission.doc_type || "passport");
          setDocNumber(data.submission.doc_number || "");
        }
      }
    } catch { /* noop */ }
  };

  useEffect(() => {
    loadKyc();
  }, []);

  const submitKyc = async () => {
    setErr(null);
    setOkMsg(null);
    setSubmitting(true);
    try {
      if (kycMethod === "nin") {
        const clean = nin.replace(/\D/g, "");
        if (clean.length !== 11) {
          setErr("NIN must be exactly 11 digits.");
          setSubmitting(false);
          return;
        }
        await api.post("/me/kyc", {
          method: "nin",
          doc_type: "nin",
          doc_number: clean,
        });
      } else {
        if (docNumber.trim().length < 2) {
          setErr("Enter your document number.");
          setSubmitting(false);
          return;
        }
        if (!docImage && kyc?.submission?.doc_image_b64 !== "[submitted]") {
          setErr("Please upload your ID document image.");
          setSubmitting(false);
          return;
        }
        await api.post("/me/kyc", {
          method: "document",
          doc_type: docType,
          doc_number: docNumber.trim(),
          doc_image_b64: docImage,
          address_image_b64: addrImage,
        });
      }
      setOkMsg("KYC submitted — awaiting review.");
      setDocImage(null);
      setAddrImage(null);
      await refreshMe();
      await loadKyc();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
      setTimeout(() => setOkMsg(null), 4000);
    }
  };

  const save = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const status = user?.kyc_status || "unverified";
  const badge = kycBadge[status] || kycBadge.unverified;
  const BadgeIcon = badge.icon;
  const canResubmit = status === "unverified" || status === "rejected";

  return (
    <DashboardLayout title="Settings" subtitle="Profile & preferences" activeKey="settings">
      <div className="grid lg:grid-cols-3 gap-4 lg:gap-5">
        <div className="lg:col-span-2 space-y-4 lg:space-y-5">
          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="font-display font-semibold text-[16px] mb-1">Personal information</div>
            <p className="text-[12px] text-white/45 mb-5">Account details from your sign-up.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] text-white/55 mb-1.5">Full name</div>
                <input
                  defaultValue={user?.name || ""}
                  readOnly
                  className="w-full h-10 px-3 rounded-lg bg-white/[0.02] border border-white/8 text-[13px] text-white/55"
                />
              </div>
              <div>
                <div className="text-[11px] text-white/55 mb-1.5">Email</div>
                <input
                  defaultValue={user?.email || ""}
                  readOnly
                  className="w-full h-10 px-3 rounded-lg bg-white/[0.02] border border-white/8 text-[13px] text-white/55"
                />
              </div>
              <div>
                <div className="text-[11px] text-white/55 mb-1.5">Account ID</div>
                <input
                  defaultValue={user?.id ? `BB-${user.id.slice(0, 8).toUpperCase()}` : ""}
                  readOnly
                  className="w-full h-10 px-3 rounded-lg bg-white/[0.02] border border-white/8 text-[13px] text-white/55 font-mono"
                />
              </div>
              <div>
                <div className="text-[11px] text-white/55 mb-1.5">Base currency</div>
                <input value="USD" readOnly className="w-full h-10 px-3 rounded-lg bg-white/[0.02] border border-white/8 text-[13px] text-white/55" />
              </div>
            </div>
          </div>

          {/* KYC verification */}
          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-[#7d8eff]" />
                <div className="font-display font-semibold text-[16px]">Identity verification (KYC)</div>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded ${badge.color}`}>
                <BadgeIcon size={11} /> {badge.label}
              </span>
            </div>
            <p className="text-[12px] text-white/45 mb-5">
              Required for withdrawals above $200. Upload a clear photo of your ID document.
            </p>

            {status === "verified" ? (
              <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
                <ShieldCheck size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-[12px] text-emerald-100/80 leading-relaxed">
                  Your identity is verified. Full trading and withdrawal access is unlocked.
                </div>
              </div>
            ) : status === "pending" ? (
              <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-amber-400/[0.08] border border-amber-400/25">
                <Clock size={15} className="text-amber-400 shrink-0 mt-0.5" />
                <div className="text-[12px] text-amber-100/80 leading-relaxed">
                  Documents submitted. We typically review within a few hours during business days.
                </div>
              </div>
            ) : null}

            {canResubmit && (
              <div className="space-y-4 mt-4" data-testid="kyc-form">
                {/* Method picker — 2 options */}
                <div>
                  <div className="text-[11px] text-white/55 mb-2">Choose verification method</div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      onClick={() => setKycMethod("nin")}
                      data-testid="kyc-method-nin"
                      className={`p-3.5 rounded-xl border text-left transition-all ${
                        kycMethod === "nin"
                          ? "border-[#1c3df5] bg-[#1c3df5]/15"
                          : "border-white/8 hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <IdCard size={15} className="text-[#7d8eff]" />
                        <span className="font-display font-semibold text-[13px] text-white">NIN only</span>
                      </div>
                      <div className="text-[11px] text-white/55 leading-relaxed">
                        Submit your 11-digit National ID number. No document upload.
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setKycMethod("document")}
                      data-testid="kyc-method-document"
                      className={`p-3.5 rounded-xl border text-left transition-all ${
                        kycMethod === "document"
                          ? "border-[#1c3df5] bg-[#1c3df5]/15"
                          : "border-white/8 hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileText size={15} className="text-[#7d8eff]" />
                        <span className="font-display font-semibold text-[13px] text-white">ID document</span>
                      </div>
                      <div className="text-[11px] text-white/55 leading-relaxed">
                        Upload a photo of your passport, ID card, or driver's licence.
                      </div>
                    </button>
                  </div>
                </div>

                {kycMethod === "nin" ? (
                  <div>
                    <div className="text-[11px] text-white/55 mb-1.5">NIN (11 digits)</div>
                    <input
                      value={nin}
                      onChange={(e) => setNin(e.target.value.replace(/\D/g, "").slice(0, 11))}
                      inputMode="numeric"
                      maxLength={11}
                      placeholder="12345678901"
                      data-testid="kyc-nin-input"
                      className="w-full h-11 px-3 rounded-lg bg-white/[0.02] border border-white/8 text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30 tabular-nums tracking-wider"
                    />
                    <div className="text-[10px] text-white/40 mt-1.5">
                      Your NIN is verified against the National Identity database. We do not store the image of your slip.
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="text-[11px] text-white/55 mb-1.5">Document type</div>
                      <div className="grid grid-cols-3 gap-2">
                        {DOC_TYPES.map((d) => (
                          <button
                            key={d.id}
                            type="button"
                            onClick={() => setDocType(d.id)}
                            data-testid={`kyc-doctype-${d.id}`}
                            className={`px-3 py-2.5 rounded-lg border text-[12px] transition-all ${
                              docType === d.id
                                ? "border-[#1c3df5] bg-[#1c3df5]/15 text-white"
                                : "border-white/8 text-white/55 hover:text-white"
                            }`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-white/55 mb-1.5">Document number</div>
                      <input
                        value={docNumber}
                        onChange={(e) => setDocNumber(e.target.value)}
                        placeholder="e.g. A1234567"
                        data-testid="kyc-doc-number-input"
                        className="w-full h-10 px-3 rounded-lg bg-white/[0.02] border border-white/8 text-[13px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <KycUploader label="ID document image" onChange={setDocImage} hasFile={!!docImage} />
                      <KycUploader label="Proof of address (optional)" onChange={setAddrImage} hasFile={!!addrImage} />
                    </div>
                  </>
                )}

                {err && (
                  <div data-testid="kyc-error" className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300">
                    <AlertCircle size={14} className="shrink-0 mt-0.5" />
                    <span>{err}</span>
                  </div>
                )}
                {okMsg && (
                  <div data-testid="kyc-success" className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-[12px] text-emerald-300">
                    <Check size={14} className="shrink-0 mt-0.5" />
                    <span>{okMsg}</span>
                  </div>
                )}
                <button
                  onClick={submitKyc}
                  disabled={submitting}
                  data-testid="kyc-submit-button"
                  className="w-full py-3 rounded-xl bb-blue text-white text-[14px] font-medium disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Submit for review"}
                </button>
              </div>
            )}
          </div>

          {/* Security */}
          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={16} className="text-[#7d8eff]" />
              <div className="font-display font-semibold text-[16px]">Security</div>
            </div>
            <p className="text-[12px] text-white/45 mb-5">Manage how you sign in.</p>
            <div className="divide-y divide-white/5">
              <div className="flex items-center justify-between py-3.5">
                <div className="flex items-center gap-3">
                  <Lock size={16} className="text-white/55" />
                  <div>
                    <div className="text-[13px] text-white font-medium">Two-factor authentication</div>
                    <div className="text-[11px] text-white/45">Coming soon.</div>
                  </div>
                </div>
                <Toggle checked={twoFA} onChange={setTwoFA} />
              </div>
              <div className="flex items-center justify-between py-3.5">
                <div>
                  <div className="text-[13px] text-white font-medium">Password</div>
                  <div className="text-[11px] text-white/45">Use the forgot-password flow to change.</div>
                </div>
                <button className="text-[12px] text-white/45">—</button>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="flex items-center gap-2 mb-1">
              <Bell size={16} className="text-[#7d8eff]" />
              <div className="font-display font-semibold text-[16px]">Notifications</div>
            </div>
            <p className="text-[12px] text-white/45 mb-5">Choose how we should reach you.</p>
            <div className="divide-y divide-white/5">
              <div className="flex items-center justify-between py-3.5">
                <div>
                  <div className="text-[13px] text-white font-medium">Email alerts</div>
                  <div className="text-[11px] text-white/45">Deposit confirmations, withdrawals.</div>
                </div>
                <Toggle checked={emailNotif} onChange={setEmailNotif} />
              </div>
              <div className="flex items-center justify-between py-3.5">
                <div>
                  <div className="text-[13px] text-white font-medium">Push notifications</div>
                  <div className="text-[11px] text-white/45">Real-time mobile updates.</div>
                </div>
                <Toggle checked={pushNotif} onChange={setPushNotif} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={save}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bb-blue text-white text-[13px] font-medium"
            >
              {saved ? <Check size={14} /> : null}
              {saved ? "Saved" : "Save preferences"}
            </button>
          </div>
        </div>

        <div>
          <div className="bb-card rounded-2xl p-5 lg:p-6">
            <div className="text-[12px] text-white/55 mb-2">Verification checklist</div>
            <div className="space-y-3 text-[13px]">
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-white/70">Account created</span>
                <Check size={15} className="text-emerald-400" />
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-white/70">Email verified</span>
                <Check size={15} className="text-emerald-400" />
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-white/70">Identity (KYC)</span>
                {status === "verified" ? (
                  <Check size={15} className="text-emerald-400" />
                ) : (
                  <span className="text-[11px] text-white/45 capitalize">{status}</span>
                )}
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-white/70">First deposit</span>
                {Number(user?.balance || 0) > 0 ? (
                  <Check size={15} className="text-emerald-400" />
                ) : (
                  <span className="text-[11px] text-white/45">Pending</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
